import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import AppShell from './components/AppShell';
import ChatContainer from './components/ChatContainer';
import SettingsPanel from './components/SettingsPanel';
import PaymentModal from './components/PaymentModal';
import LoginPage from './components/LoginPage';
import VerifyPassword from './components/VerifyPassword';
import GraphView from './graph_components/GraphView';
import LandingView from './views/LandingView';

import { UserProvider, useUserContext } from './contexts/UserContext';
import { TripProvider, useTripContext } from './contexts/TripContext';
import { sendChatMessage, sendChatMessageStream, resetSession, confirmPayment as confirmPaymentApi, createSession, getDefaultCard, getAllPaymentCards, updateGraphVisibility } from './utils/apiService';


const mockPaymentData = {
  command: 'credential_required',
  hotel_name: 'Grand Hotel Singapore',
  room_type: 'Deluxe Suite',
  check_in_date: '2024-12-25',
  check_out_date: '2024-12-28',
  total_price: 1250.00
  // card_number removed - will be provided by PaymentModal
};

function AppContent() {
  const { userInfo, sessionId, updateUserInfo, updateSessionId, clearUserInfo } = useUserContext();
  const { currentView, initialQuery, goToLanding } = useTripContext();
  const [isPasswordVerified, setIsPasswordVerified] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState('');
  const [messages, setMessages] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isGraphViewOpen, setIsGraphViewOpen] = useState(false); // Graph view side panel
  const [isChatHidden, setIsChatHidden] = useState(false); // Hide chat and show graph fullscreen
  const messageIdCounter = React.useRef(0);
  const inactivityTimerRef = useRef(null);
  const activityTimeoutRef = useRef(null);

  // 15 minutes in milliseconds
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

  const handleLogout = React.useCallback(async () => {
    // Clear session data on backend
    if (sessionId) {
      try {
        await resetSession(sessionId);
      } catch (error) {
        console.error('Failed to clear session on backend:', error);
      }
    }

    // Clear frontend state
    clearUserInfo();
    setIsLoggedIn(false);
    setMessages([]);
    setUserId('');
    setOrderId(null);
    setPaymentModalData(null);
    updateSessionId(null);

    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
  }, [sessionId, clearUserInfo]);

  // Reset inactivity timer
  const resetInactivityTimer = React.useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    if (isLoggedIn) {
      activityTimeoutRef.current = setTimeout(() => {
        handleLogout();
      }, INACTIVITY_TIMEOUT);
    }
  }, [isLoggedIn, handleLogout]);

  // Track user activity
  useEffect(() => {
    if (!isLoggedIn) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [isLoggedIn, resetInactivityTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, []);

  // Track if initial query has been sent
  const initialQuerySent = useRef(false);

  // Send initial query when entering chat from landing page
  useEffect(() => {
    console.log('Sending initial query:', initialQuery);

    if (currentView === 'chat' && initialQuery && !initialQuerySent.current && isLoggedIn) {

      initialQuerySent.current = true;
      // Small delay to ensure the chat interface is ready
      setTimeout(() => {
        sendMessage(initialQuery);
      }, 100);
    }
    // Reset the flag when going back to landing
    if (currentView === 'landing') {
      initialQuerySent.current = false;
    }
  }, [currentView, initialQuery, isLoggedIn]);

  const generateMessageId = () => {
    messageIdCounter.current += 1;
    return `msg_${Date.now()}_${messageIdCounter.current}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const addMessage = (content, isUser = false, isTyping = false, widgets = null, agentStates = null) => {
    const newMessage = {
      id: generateMessageId(),
      content,
      isUser,
      isTyping,
      widgets,
      agentStates: agentStates || []
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  };

  const updateMessage = (messageId, content, widgets = null, streamChunks = null, agentStates = null) => {
    setMessages(prev =>
      prev.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            content,
            isTyping: false,
            widgets,
            streamChunks,
            agentStates: agentStates !== null ? agentStates : (msg.agentStates || [])
          };
        }
        return msg;
      })
    );
  };

  const sendMessage = async (query) => {
    console.log('Sending message:', query);
    if (!query.trim()) return;

    // Reset inactivity timer on user activity
    resetInactivityTimer();

    // Add user message
    addMessage(query, true);

    // Add typing indicator
    const typingId = addMessage('', false, true);

    try {
      // Try streaming first, fallback to non-streaming
      const streamChunks = [];

      const handleStreamChunk = (chunk) => {
        // error type chunk, record but not process 
        if (!chunk || typeof chunk !== 'object') {
          console.warn('⚠️ Received invalid chunk:', chunk);
          return;
        }

        if (chunk.error) {
          console.error('❌ Received error chunk:', chunk.error);
          updateMessage(typingId, `**Error:** ${chunk.error}`, null, [...streamChunks]);
          return;
        }

        // agent_state type chunk
        if (chunk.type === 'agent_state') {
          // console.log('🔍 Received agent_state chunk:', chunk);
          setMessages(prev =>
            prev.map(msg =>
              msg.id === typingId
                ? {
                  ...msg,
                  agentStates: [...(msg.agentStates || []), {
                    state: chunk.state,
                    tool_name: chunk.tool_name,
                    message: chunk.message,
                    timestamp: Date.now()
                  }]
                }
                : msg
            )
          );
          return;
        }

        if (chunk.type === 'status') {
          return; // status message is not added to streamChunks
        }

        streamChunks.push(chunk);
        // Update message with current stream chunks in real-time
        // Extract widgets from current chunks
        try {
          setMessages(prev => {
            const currentMsg = prev.find(m => m.id === typingId);
            const currentAgentStates = currentMsg?.agentStates || [];
            return prev.map(msg =>
              msg.id === typingId
                ? {
                  ...msg,
                  streamChunks: [...streamChunks],
                  agentStates: currentAgentStates
                }
                : msg
            );
          });
        } catch (err) {
          console.warn('⚠️ Extract widgets failed:', err);
        }
      };

      const handleStreamError = (error) => {
        console.error('[App] Streaming error:', error);
      };

      const handleStreamComplete = () => {
        // console.log(`📊 Stream completed. Total chunks: ${streamChunks.length}`);
        // Extract message and widgets from stream chunks
        const textChunks = streamChunks
          .filter(chunk => {
            // New protocol format: {"parent": "root", "component": {...}}
            // Default to 'add' operation if chunk has parent and component
            const chunkType = chunk.type || (chunk.parent && chunk.component ? 'add' : null);
            // Check if it's a Text component
            if (chunkType === 'add' && chunk.component && chunk.component.type === 'Text') {
              return true;
            }
            return chunk.type === 'text' || chunk.type === 'Text';
          })
          .map(chunk => {
            if (chunk.component && chunk.component.props) {
              return chunk.component.props.text || '';
            }
            return chunk.props?.text || chunk.text || '';
          })
          .join('');

        console.log(`📨 Final message from stream: ${textChunks}`);
        setMessages(prev => {
          const currentMsg = prev.find(m => m.id === typingId);
          const currentAgentStates = currentMsg?.agentStates || [];
          return prev.map(msg =>
            msg.id === typingId
              ? {
                ...msg,
                content: textChunks,
                isTyping: false,
                agentStates: currentAgentStates
              }
              : msg
          );
        });
      };

      // Try streaming
      await sendChatMessageStream(
        query,
        sessionId,
        userId,
        handleStreamChunk,
        handleStreamError,
        handleStreamComplete
      );
    } catch (error) {
      // If streaming fails, try non-streaming
      try {
        const response = await sendChatMessage(query, sessionId, userId);
        const message = response.message || '';
        const widgets = response.widgets || [];
        updateMessage(typingId, message, widgets);
      } catch (fallbackError) {
        updateMessage(typingId, `**Request failed:** ${fallbackError.message}`);
      }
    }
  };

  // Helper to build component map from tree
  const buildComponentMap = (node, map) => {
    if (!node) return;
    map.set(node.id, node);
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => {
        if (typeof child === 'object' && child.id) {
          buildComponentMap(child, map);
        }
      });
    }
  };

  // Helper to convert component to widget format
  const convertComponentToWidget = (component, componentMap) => {
    const typeMapping = {
      'Text': 'text',
      'Button': 'button',
      'List': 'list',
      'Card': 'card',
      'Carousel': 'carousel',
      'Form': 'form',
      'Input': 'input',
      'Container': 'container',
      'Row': 'list' // Row is treated as a list item
    };

    const widgetType = typeMapping[component.type] || component.type.toLowerCase();

    // Extract all properties from component (except id, type, children, props)
    // Properties like text, label, variant, etc. are directly on component object
    const { id, type, children, props: nestedProps, items, ...directProps } = component;

    // Merge: direct properties first (from component object), then nested props (if exists)
    const props = {
      ...directProps,  // Properties directly on component (e.g., text, label, variant)
      ...(nestedProps || {})  // Properties in props object (backward compatibility)
    };

    const widget = {
      id: component.id,
      type: widgetType,
      props: props
    };

    // Handle children - children can be an array of IDs (from parent-child mapping) or array of objects
    if (component.children && Array.isArray(component.children)) {
      widget.children = component.children
        .map(childIdOrObj => {
          // If child is an ID string, look it up in componentMap
          // If child is already an object, convert it directly
          let childComponent;
          if (typeof childIdOrObj === 'string') {
            childComponent = componentMap.get(childIdOrObj);
          } else if (typeof childIdOrObj === 'object' && childIdOrObj.id) {
            childComponent = childIdOrObj;
          }

          return childComponent ? convertComponentToWidget(childComponent, componentMap) : null;
        })
        .filter(Boolean);
    }

    // Handle items property for List/Card/Carousel
    // Items can be an array of IDs (from backend) or array of objects
    // If component has children (from parent-child mapping), use children instead of items
    // Otherwise, if items is an array of IDs, look them up and convert to objects
    if ((widgetType === 'list' || widgetType === 'card' || widgetType === 'carousel') && items && Array.isArray(items)) {
      // If we have children from parent-child mapping, use those (more reliable)
      if (widget.children && widget.children.length > 0) {
        // Convert children widgets to item objects
        widget.props.items = widget.children.map(childWidget => childWidget.props);
        // Clear children since we're using items instead
        widget.children = undefined;
      } else if (items.length > 0 && typeof items[0] === 'string') {
        // items is an array of IDs - convert to actual component objects
        const itemObjects = items
          .map(itemId => {
            const itemComponent = componentMap.get(itemId);
            if (itemComponent) {
              // Convert Row/Card component to item object format
              const itemWidget = convertComponentToWidget(itemComponent, componentMap);
              // Extract item properties from widget props
              return itemWidget.props;
            }
            return null;
          })
          .filter(Boolean);

        // Set items in props (for List/Card/Carousel components)
        widget.props.items = itemObjects;
      } else {
        // items is already an array of objects, use as is
        widget.props.items = items;
      }
    }

    return widget;
  };

  const resetConversation = async () => {
    if (!window.confirm('Are you sure you want to reset the conversation?')) {
      return;
    }

    if (sessionId) {
      try {
        await resetSession(sessionId);
      } catch (error) {
        console.error('Reset failed:', error);
      }
    }

    setMessages([]);
    setOrderId(null);

    // Create a new session on backend
    if (userInfo && userInfo.name) {
      try {
        const sessionResponse = await createSession(userInfo.name, userInfo.userType);
        updateSessionId(sessionResponse.session_id);
        setUserId(sessionResponse.user_id || userInfo.name);
      } catch (error) {
        console.error('Failed to create new session:', error);
      }
    }
  };

  const openPaymentModal = (paymentData) => {
    setPaymentModalData(paymentData);
  };

  const closePaymentModal = () => {
    setPaymentModalData(null);
  };

  // Handle A2UI events (button clicks, form submissions, etc.)
  const handleA2UIEvent = async (event, message) => {
    console.log('A2UI Event:', event);
    // Option 2: Convert event to message (for simple events)
    await _convertEventToMessage(event, message);
  };

  // Handle confirm payment action
  const handleConfirmPayment = (event, message) => {
    console.log('event.data', event.data);
    console.log('Full event object:', event);
    // console.log('Message object:', message);

    // Get card info from user context (stored during login)
    const cardInfo = userInfo?.cardInfo;
    console.log('Card info from user context:', cardInfo);

    // Extract order_id from event.data
    // event.data can be: string (cart_mandate_id), object, or null/undefined
    let orderIdValue = '';
    if (event.data) {
      if (typeof event.data === 'string') {
        // If data is a string, use it directly as order_id
        orderIdValue = event.data;
      } else if (typeof event.data === 'object') {
        // If data is an object, try to extract order_id, orderId, or cart_mandate_id
        orderIdValue = event.data.order_id || event.data.orderId || event.data.cart_mandate_id || event.data.order || '';
      }
    }

    // Also check event object itself for order_id
    if (!orderIdValue) {
      orderIdValue = event.orderId || event.order || '';
    }

    setOrderId(orderIdValue);


    let totalPrice = event.amount || 0;
    let currency = event.currency || 'USD';

    if (event.totalPrice && typeof event.totalPrice === 'string') {
      totalPrice = parseFloat(event.totalPrice.replace(/[^0-9.-]/g, '')) || 0;
    }
    if (event.currency && typeof event.currency === 'string') {
      currency = event.currency;
    }

    console.log('Total price:', totalPrice);
    console.log('Currency:', currency);

    // // Extract total price from widget with title "Order Summary" in message
    // let totalPrice = null;
    // let currency = 'USD'; // Default currency

    // if (message && message.widgets && Array.isArray(message.widgets)) {
    //   // Find widget with props.title === "Order Summary"
    //   const orderSummaryWidget = message.widgets.find(
    //     widget => (widget.type === 'card' || widget.type === 'cards') && 
    //               widget.props && 
    //               widget.props.title === 'Order Summary'
    //   );

    //   if (orderSummaryWidget && orderSummaryWidget.props) {
    //     const items = orderSummaryWidget.props.items || [];

    //     if (items.length > 0) {
    //       // Calculate total price by summing all items
    //       let calculatedTotal = 0;

    //       items.forEach((item, index) => {
    //         // Extract price from item (support multiple field names)
    //         const itemPrice = item.price || item.price_value || 0;
    //         const itemPriceNum = typeof itemPrice === 'string' 
    //           ? parseFloat(itemPrice.replace(/[^0-9.-]/g, '')) || 0
    //           : (typeof itemPrice === 'number' ? itemPrice : 0);

    //         calculatedTotal += itemPriceNum;

    //         // Get currency from first item (assuming all items use same currency)
    //         if (index === 0) {
    //           currency = item.currency || 
    //                     item.price_currency || 
    //                     item.currency_code || 
    //                     'USD';
    //         }
    //       });

    //       totalPrice = calculatedTotal;
    //     }
    //   }
    // }

    // Merge payment data with card info and total price
    const paymentDataWithCard = {
      order_id: orderIdValue,
      card_number: cardInfo?.cardNumber,
      card_holder: cardInfo?.cardHolder,
      expiry_date: cardInfo?.expiryDate,
      ...(totalPrice != null && {
        order_price: totalPrice,
        order_currency: currency
      })
    };
    console.log('Setting paymentModalData:', paymentDataWithCard);
    setPaymentModalData(paymentDataWithCard);
  };

  // Helper function to convert events to messages
  const _convertEventToMessage = async (event, message) => {
    switch (event.type) {
      case 'suggestion_click':
        if (event.suggestion) {
          await sendMessage(event.suggestion);
        }
        break;

      case 'button_click':
        const action = event.action;
        if (action === 'confirm_payment') {
          handleConfirmPayment(event, message);
        } else if (action === 'edit_flights') {
          // User clicks Edit button - agent will show options to edit outbound or return
          await sendMessage('I want to edit my flight selection');
        } else if (action === 'edit_outbound') {
          // User wants to change outbound flight selection
          await sendMessage('I want to change my outbound flight selection');
        } else if (action === 'edit_return') {
          // User wants to change return flight selection
          await sendMessage('I want to change my return flight selection');
        } else if (action === 'book_hotel') {
          // User wants to book a hotel after flight confirmation
          const city = event.data?.city || 'the destination';
          const checkIn = event.data?.check_in || '';
          const checkOut = event.data?.check_out || '';
          if (checkIn && checkOut) {
            await sendMessage(`I want to book a hotel in ${city} from ${checkIn} to ${checkOut}`);
          } else {
            await sendMessage(`I want to book a hotel in ${city}`);
          }
        } else if (action === 'select_hotel') {
          // User selects a hotel
          const hotelName = event.data?.hotel_name || event.data?.name || 'this hotel';
          await sendMessage(`I select ${hotelName}`);
        } else if (action === 'confirm_and_pay') {
          // User confirms booking and wants to pay (flight or hotel)
          console.log('🎯 confirm_and_pay action received!');
          const paymentDataFromEvent = event.data || {};
          console.log('  paymentDataFromEvent:', paymentDataFromEvent);
          const total = paymentDataFromEvent.total || paymentDataFromEvent.amount || 0;
          const currency = paymentDataFromEvent.currency || 'SGD';
          const bookingType = paymentDataFromEvent.booking_type || 'flight';

          if (bookingType === 'hotel') {
            // Hotel booking payment
            const bookingId = paymentDataFromEvent.booking_id || '';
            const hotelName = paymentDataFromEvent.hotel_name || '';
            const roomType = paymentDataFromEvent.room_type || '';
            const checkIn = paymentDataFromEvent.check_in || '';
            const checkOut = paymentDataFromEvent.check_out || '';

            // Set order ID for payment tracking
            const orderIdValue = `HOTEL-${bookingId}-${Date.now()}`;
            setOrderId(orderIdValue);

            // Open Payment Modal with hotel booking details
            setPaymentModalData({
              order_id: orderIdValue,
              order_price: total,
              order_currency: currency,
              booking_type: 'hotel',
              booking_id: bookingId,
              hotel_name: hotelName,
              room_type: roomType,
              check_in: checkIn,
              check_out: checkOut
            });
          } else {
            // Flight booking payment (default)
            const outboundId = paymentDataFromEvent.outbound_id || paymentDataFromEvent.outbound || '';
            const returnId = paymentDataFromEvent.return_id || paymentDataFromEvent.return || '';

            // Set order ID for payment tracking
            const orderIdValue = `FLIGHT-${outboundId}-${returnId}-${Date.now()}`;
            setOrderId(orderIdValue);

            // Open Payment Modal with flight booking details
            setPaymentModalData({
              order_id: orderIdValue,
              order_price: total,
              order_currency: currency,
              booking_type: 'flight',
              outbound_id: outboundId,
              return_id: returnId
            });
          }
        } else if (action === 'confirm_flight_booking') {
          // Legacy: Confirm flight booking action
          const paymentData = event.data || {};
          const total = paymentData.total || paymentData.amount || '';
          const currency = paymentData.currency || 'SGD';
          await sendMessage(`I confirm booking the flights for ${currency} ${total}`);
        } else {
          // Handle button clicks - convert to natural language message
          if (event.label) {
            const labelLower = event.label.toLowerCase();
            if (labelLower.includes('book') || labelLower.includes('reserve')) {
              await sendMessage(`I want to book ${event.label}`);
            } else if (labelLower.includes('payment') && labelLower.includes('confirm')) {
              // openPaymentModal(event.data); 
            } else {
              await sendMessage(event.label);
            }
          }
        }
        break;

      case 'item_click':
      case 'card_click':
        if (event.item) {
          const itemTitle = event.item.title || event.item.id;
          if (itemTitle) {
            await sendMessage(`${itemTitle}`);
          }
        }
        break;

      case 'destination_click':
        // Handle destination card click (from DestinationCarousel)
        if (event.destination) {
          const city = event.destination.city || event.destination.title || event.destination.name;
          if (city) {
            await sendMessage(`I'm interested in a trip to ${city}`);
          }
        }
        break;

      case 'date_change':
        // Handle date change from FlightDateSelector
        if (event.data) {
          const { date, direction, origin, destination, originCity, destinationCity } = event.data;
          const displayOrigin = originCity || origin;
          const displayDest = destinationCity || destination;
          const directionText = direction === 'return' ? 'return' : 'outbound';

          await sendMessage(`Search ${directionText} flights for ${date} from ${displayOrigin} (${origin}) to ${displayDest} (${destination})`);
        }
        break;

      case 'flight_select':
        // Handle flight selection (user is choosing/selecting a flight option)
        if (event.flightData || event.data) {
          const flightData = event.flightData || event.data;
          const flightInfo = flightData.flightNumber || flightData.flight_number || '';
          const airline = flightData.airline || '';
          const departureTime = flightData.departureTime || flightData.departure_time || '';
          const arrivalTime = flightData.arrivalTime || flightData.arrival_time || '';

          if (flightInfo) {
            // Selection message - not booking yet, just selecting
            await sendMessage(`I select flight ${airline} ${flightInfo} departing at ${departureTime}`);
          } else {
            await sendMessage(`I select this flight`);
          }
        }
        break;

      case 'flight_book':
        // Handle flight booking confirmation (user confirms to book)
        if (event.flightData || event.data) {
          const flightData = event.flightData || event.data;
          const flightInfo = flightData.flightNumber || flightData.flight_number || '';
          const airline = flightData.airline || '';
          const price = flightData.price || '';
          const currency = flightData.currency || 'SGD';

          if (flightInfo) {
            // Confirm booking message
            await sendMessage(`I confirm booking flight ${airline} ${flightInfo} for ${currency} ${price}`);
          } else {
            await sendMessage(`I confirm booking this flight`);
          }
        }
        break;

      case 'hotel_select':
        // Handle hotel selection (user is choosing/selecting a hotel option)
        if (event.hotelData || event.data) {
          const hotelData = event.hotelData || event.data;
          const hotelName = hotelData.name || hotelData.title || '';
          const roomType = hotelData.roomType || hotelData.room_type || '';

          if (hotelName) {
            await sendMessage(`I select ${hotelName}${roomType ? ` - ${roomType}` : ''}`);
          } else {
            await sendMessage(`I select this hotel`);
          }
        }
        break;

      case 'view_rooms':
        // Handle "View Rooms" button click - show available rooms for a hotel
        if (event.data || event.hotelData) {
          const data = event.data || event.hotelData;
          const hotelName = data.name || data.hotelName || '';
          const hotelId = event.hotelId || data.hotelId || '';

          if (hotelName) {
            await sendMessage(`Show me rooms for ${hotelName}`);
          } else if (hotelId) {
            await sendMessage(`Show me rooms for hotel ${hotelId}`);
          } else {
            await sendMessage(`Show me rooms for this hotel`);
          }
        }
        break;

      case 'select_room':
        // Handle room selection - user selects a specific room
        if (event.data || event.hotelData) {
          const data = event.data || event.hotelData;
          const roomType = data.roomType || data.name || '';
          const hotelName = data.hotelName || '';
          const roomId = event.roomId || data.roomId || '';

          if (roomType && hotelName) {
            await sendMessage(`I select ${roomType} at ${hotelName}`);
          } else if (roomType) {
            await sendMessage(`I select room: ${roomType}`);
          } else if (roomId) {
            await sendMessage(`I select room ${roomId}`);
          } else {
            await sendMessage(`I select this room`);
          }
        }
        break;

      case 'hotel_book':
        // Handle hotel booking confirmation (user confirms to book)
        if (event.hotelData || event.data) {
          const hotelData = event.hotelData || event.data;
          const hotelName = hotelData.name || hotelData.title || '';
          const roomType = hotelData.roomType || hotelData.room_type || '';
          const price = hotelData.price || '';
          const currency = hotelData.currency || 'SGD';

          if (hotelName) {
            await sendMessage(`I confirm booking ${hotelName}${roomType ? ` - ${roomType}` : ''} for ${currency} ${price}`);
          } else {
            await sendMessage(`I confirm booking this hotel`);
          }
        }
        break;

      case 'form_submit':
        // Handle form submissions
        if (event.formId === 'payment_form') {
           // Handle payment form submission
            openPaymentModal(event.data);
        } else {
          // Send form data as a message
          const formMessage = Object.entries(event.data)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          await sendMessage(formMessage);
        }
        break;

      case 'view_itinerary':
        // Handle "View Full Itinerary" button click - show complete travel itinerary
        await sendMessage('Show me my full itinerary');
        break;

      case 'more_hotels':
        // Handle "More Hotels" button click - show additional hotel options
        if (event.data) {
          const city = event.data.city || '';
          const checkIn = event.data.check_in || '';
          const checkOut = event.data.check_out || '';
          await sendMessage(`Show me more hotels in ${city} from ${checkIn} to ${checkOut}`);
        } else {
          await sendMessage('Show me more hotel options');
        }
        break;

      default:
        console.log('Unhandled A2UI event:', event);
    }
  };

  const confirmPayment = async (password, isCashBack = false, cardData = null) => {
    /*if (!password) {
      alert('Please enter the payment password');
      return false;
    }*/

    try {
      // call the confirm payment api
      const data = await confirmPaymentApi(
        password,
        sessionId,
        userId,
        paymentModalData,
        orderId,
        isCashBack
      );

      closePaymentModal();

      // Extract card info from userInfo (pre-fetched during login)
      const cardNumber = userInfo?.cardInfo?.cardNumber || '';
      console.log('cardNumber from userInfo:', userInfo?.cardInfo);
      console.log('Using card number for confirmation message:', cardNumber);
      const maskedCard = cardNumber ? `****${cardNumber.slice(-4)}` : '****1234';
      console.log('Using card number for confirmation message:', cardNumber);

      const cardType = userInfo?.cardInfo?.card_type || 'Card';
      const cardBenefit = (isCashBack && cardType === 'mastercard') ? '5% Cashback' : '';

      // Check booking type and send appropriate confirmation message
      if (paymentModalData?.booking_type === 'flight') {
        const outboundId = paymentModalData.outbound_id || '';
        const returnId = paymentModalData.return_id || '';
        const currency = paymentModalData.order_currency || 'SGD';
        const total = paymentModalData.order_price || 0;

        // Send payment confirmation message with card info
        await sendMessage(`Payment confirmed with ${cardType} ${maskedCard}${cardBenefit ? ` (${cardBenefit})` : ''}. Booked flights ${outboundId} and ${returnId} for ${currency} ${total}`);
      } else if (paymentModalData?.booking_type === 'hotel') {
        const bookingId = paymentModalData.booking_id || '';
        const hotelName = paymentModalData.hotel_name || '';
        const currency = paymentModalData.order_currency || 'SGD';
        const total = paymentModalData.order_price || 0;

        // Send hotel payment confirmation message with card info
        await sendMessage(`Payment confirmed with ${cardType} ${maskedCard}${cardBenefit ? ` (${cardBenefit})` : ''}. Booking ${bookingId} for ${hotelName} confirmed for ${currency} ${total}`);
      } else {
        // Default: send credential verified message with card info
        await sendMessage(`Payment confirmed with ${cardType} ${maskedCard}${cardBenefit ? ` (${cardBenefit})` : ''}`);
      }
      return true;
    } catch (error) {
      alert(error.message || 'Request failed');
      return false;
    }
  };

  const testPaymentModal = () => {
    openPaymentModal(mockPaymentData);
  };

  const testCredentialRequiredResponse = () => {
    const mockMessage = 'Your booking is ready. Please confirm your payment to complete the reservation.';
    const mockStructuredData = {
      command: 'credential_required',
      hotel_name: 'Marina Bay Sands',
      room_type: 'Premium King Room',
      check_in_date: '2024-12-20',
      check_out_date: '2024-12-23',
      total_price: 850.50
      // card_number removed - will be provided by PaymentModal
    };

    addMessage(mockMessage, false, false, mockStructuredData);
  };


  const handlePasswordVerified = (data) => {
    setIsPasswordVerified(true);
  };

  const handleLogin = async (loginData) => {
    try {
      // Create session on backend
      const sessionResponse = await createSession(loginData.name, '');
      updateSessionId(sessionResponse.session_id);
      const userIdValue = sessionResponse.user_id || loginData.name;
      setUserId(userIdValue);

      // Pre-fetch all cards and default card during login for faster payment modal
      let cardInfo = null;
      let allCards = [];
      let defaultCardId = null;

      try {
        // Fetch cards in parallel for speed
        const [defaultCardResponse, cardsResponse] = await Promise.all([
          getDefaultCard(userIdValue),
          getAllPaymentCards(userIdValue)
        ]);

        allCards = cardsResponse || [];
        defaultCardId = defaultCardResponse?.default_card_id;

        if (defaultCardId && allCards.length > 0) {
          const defaultCard = allCards.find(card => card.card_id === defaultCardId);
          if (defaultCard) {
            cardInfo = defaultCard;
          }
        }

        console.log('Pre-fetched cards on login:', allCards.length, 'cards, default:', defaultCardId);
      } catch (error) {
        console.error('Failed to pre-fetch cards:', error);
      }

      // Store user info with pre-fetched cards
      updateUserInfo({
        name: loginData.name,
        userType: 'budget',
        cardInfo: cardInfo,
        allCards: allCards,
        defaultCardId: defaultCardId
      });

      setIsLoggedIn(true);
    } catch (error) {
      console.error('Login failed:', error);
      alert(error.message || 'Login failed');
    }
  };

  // Show verify password page first
  if (!isPasswordVerified) {
    return (
      <div className="App">
        <VerifyPassword onVerified={handlePasswordVerified} />
      </div>
    );
  }

  // Show login page after password is verified
  if (!isLoggedIn) {
    return (
      <div className="App">
        <LoginPage onLogin={handleLogin} />

      </div>
    );
  }

  // Show Landing View or Chat View based on currentView from TripContext
  // Both wrapped in AppShell for unified navbar
  if (currentView === 'landing') {
    return (
      <div className="App landing-mode">
        <AppShell
          showBackButton={false}
          showAgentStatus={false}
          userName={userInfo?.name}
        >
          <LandingView />
        </AppShell>
      </div>
    );
  }

  // Chat View - wrapped in AppShell with back button and agent status
  return (
    <div className={`App chat-mode ${isGraphViewOpen ? 'graph-view-open' : ''} ${isChatHidden ? 'chat-hidden' : ''}`}>
      <AppShell
        showBackButton={true}
        onBackClick={goToLanding}
        showAgentStatus={true}
        onToggleSettings={() => setIsSettingsOpen(!isSettingsOpen)}
        onNavigateToGraph={() => {
          setIsGraphViewOpen(true);
          updateGraphVisibility(sessionId, true);
        }}
        onCloseGraph={() => {
          setIsGraphViewOpen(false);
          updateGraphVisibility(sessionId, false);
        }}
        isGraphViewOpen={isGraphViewOpen}
        userName={userInfo?.name}
      >
        <div className="chat-layout">
          {!isChatHidden && (
            <div className={`chat-area ${isGraphViewOpen ? 'with-graph' : ''}`}>
                <ChatContainer
                messages={messages}
                onSendMessage={sendMessage}
                onOpenPaymentModal={openPaymentModal}
                onA2UIEvent={handleA2UIEvent}
                userName={userInfo?.name}
                paymentModalData={paymentModalData}
                onClosePaymentModal={closePaymentModal}
                onConfirmPayment={confirmPayment}
                userId={userId}
                sessionId={sessionId}
                userInfo={userInfo}
                updateUserInfo={updateUserInfo}
              />
            </div>
          )}

          {isGraphViewOpen && (
            <div className={`graph-area ${isChatHidden ? 'fullscreen' : ''}`}>
              <GraphView
                sessionId={sessionId}
                userId={userId}
                isChatHidden={isChatHidden}
                onBack={() => {
                  if (isChatHidden) {
                    setIsChatHidden(false);
                  } else {
                    setIsGraphViewOpen(false);
                    updateGraphVisibility(sessionId, false);
                  }
                }}
                onHideChat={() => setIsChatHidden(true)}
              />
            </div>
          )}
        </div>
      </AppShell>

      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        sessionId={sessionId}
        setSessionId={updateSessionId}
        userId={userId}
        setUserId={setUserId}
        onTestPaymentModal={testPaymentModal}
        onTestCredentialResponse={testCredentialRequiredResponse}
        userInfo={userInfo}
        updateUserInfo={updateUserInfo}
        updateSessionId={updateSessionId}
      />

      {/* PaymentModal is now rendered inline inside ChatContainer */}
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <TripProvider>
        <AppContent />
      </TripProvider>
    </UserProvider>
  );
}

export default App;
